import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Trash2,
  Globe,
  AlertTriangle,
  X,
  Shield,
  ShieldAlert,
  ToggleLeft,
  ToggleRight,
  Edit2,
  Asterisk,
  Link2,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import type { SSOOrigin } from "@/types/admin";
import adminService from "@/services/admin-service";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";

export default function SSOOriginsPage() {
  const navigate = useNavigate();
  const { isOrgAdmin } = useAuthStore();

  // Check if user is org admin or super admin
  const canAccess = isOrgAdmin();
  const [origins, setOrigins] = useState<SSOOrigin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrigin, setEditingOrigin] = useState<SSOOrigin | null>(null);
  const [creating, setCreating] = useState(false);
  const [showGlobalOnly, setShowGlobalOnly] = useState(false);
  const [formData, setFormData] = useState({
    origin_pattern: "",
    pattern_type: "exact" as "exact" | "wildcard",
    description: "",
    is_active: true,
    org_id: null as string | null,
  });

  useEffect(() => {
    loadOrigins();
  }, [showGlobalOnly]);

  const loadOrigins = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listSSOOrigins({
        include_global: true,
        active_only: false,
      });
      setOrigins(data.origins || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load SSO origins");
      console.error(err);
      setOrigins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.origin_pattern) {
      setError("Please provide an origin pattern");
      return;
    }

    try {
      setCreating(true);
      setError(null);

      if (editingOrigin) {
        await adminService.updateSSOOrigin(editingOrigin.id, {
          origin_pattern: formData.origin_pattern,
          pattern_type: formData.pattern_type,
          description: formData.description || undefined,
          is_active: formData.is_active,
        });
      } else {
        await adminService.createSSOOrigin({
          origin_pattern: formData.origin_pattern,
          pattern_type: formData.pattern_type,
          description: formData.description || undefined,
          is_active: formData.is_active,
          org_id: formData.org_id,
        });
      }

      await loadOrigins();
      closeModal();
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          `Failed to ${editingOrigin ? "update" : "create"} SSO origin`,
      );
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (origin: SSOOrigin) => {
    try {
      setError(null);
      await adminService.toggleSSOOrigin(origin.id, !origin.is_active);
      await loadOrigins();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to toggle SSO origin");
      console.error(err);
    }
  };

  const handleDelete = async (origin: SSOOrigin) => {
    if (
      !confirm(
        `Are you sure you want to delete the SSO origin "${origin.origin_pattern}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setError(null);
      await adminService.deleteSSOOrigin(origin.id);
      await loadOrigins();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete SSO origin");
      console.error(err);
    }
  };

  const openEditModal = (origin: SSOOrigin) => {
    setEditingOrigin(origin);
    setFormData({
      origin_pattern: origin.origin_pattern,
      pattern_type: origin.pattern_type,
      description: origin.description || "",
      is_active: origin.is_active,
      org_id: origin.org_id || null,
    });
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingOrigin(null);
    setFormData({
      origin_pattern: "",
      pattern_type: "exact",
      description: "",
      is_active: true,
      org_id: null,
    });
    setError(null);
  };

  const filteredOrigins = origins.filter((origin) => {
    const matchesSearch =
      origin.origin_pattern.toLowerCase().includes(searchTerm.toLowerCase()) ||
      origin.description?.toLowerCase().includes(searchTerm.toLowerCase());

    if (showGlobalOnly) {
      return matchesSearch && !origin.org_id;
    }
    return matchesSearch;
  });

  const globalOrigins = filteredOrigins.filter((o) => !o.org_id);
  const orgOrigins = filteredOrigins.filter((o) => o.org_id);

  // Show 403 if not org admin
  if (!canAccess) {
    return (
      <div className="-mt-20 flex min-h-screen items-center justify-center">
        <Card className="max-w-md border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody className="py-12 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <ShieldAlert className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-red-900 dark:text-red-100">
              Admin Access Required
            </h3>
            <p className="mb-2 text-red-700 dark:text-red-300">
              Only organization administrators can access SSO Origins
              management.
            </p>
            <p className="mb-6 text-sm text-red-600 dark:text-red-400">
              This page manages allowed SSO domains and requires elevated
              privileges.
            </p>
            <button
              onClick={() => navigate({ to: "/admin" })}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-red-700"
            >
              Go to Dashboard
            </button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative">
          <div className="mb-2 flex items-center gap-3">
            <Shield className="h-8 w-8" />
            <h1 className="text-4xl font-bold">SSO Allowed Origins</h1>
          </div>
          <p className="text-lg text-indigo-100">
            Manage which domains can use SSO authentication with your
            application
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search origins..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={showGlobalOnly}
              onChange={(e) => setShowGlobalOnly(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Global only
          </label>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4" />
          Add Origin
        </button>
      </div>

      {/* Origins List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
        </div>
      ) : filteredOrigins.length === 0 ? (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <Globe className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                No SSO origins found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "Try a different search term"
                  : "Add your first allowed origin to get started"}
              </p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Global Origins Section */}
          {globalOrigins.length > 0 && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <Globe className="h-5 w-5 text-indigo-500" />
                Global Origins
                <span className="text-sm font-normal text-gray-500">
                  ({globalOrigins.length})
                </span>
              </h2>
              <div className="grid gap-4">
                {globalOrigins.map((origin) => (
                  <OriginCard
                    key={origin.id}
                    origin={origin}
                    onEdit={() => openEditModal(origin)}
                    onToggle={() => handleToggle(origin)}
                    onDelete={() => handleDelete(origin)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Organization Origins Section */}
          {orgOrigins.length > 0 && !showGlobalOnly && (
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <Shield className="h-5 w-5 text-purple-500" />
                Organization Origins
                <span className="text-sm font-normal text-gray-500">
                  ({orgOrigins.length})
                </span>
              </h2>
              <div className="grid gap-4">
                {orgOrigins.map((origin) => (
                  <OriginCard
                    key={origin.id}
                    origin={origin}
                    onEdit={() => openEditModal(origin)}
                    onToggle={() => handleToggle(origin)}
                    onDelete={() => handleDelete(origin)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-gray-800">
            <div className="border-b border-gray-200 p-6 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingOrigin ? "Edit SSO Origin" : "Add SSO Origin"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-6 p-6">
              {/* Pattern Type */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Pattern Type
                </label>
                <div className="flex gap-4">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="pattern_type"
                      value="exact"
                      checked={formData.pattern_type === "exact"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pattern_type: e.target.value as "exact" | "wildcard",
                        })
                      }
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <Link2 className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Exact URL
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="pattern_type"
                      value="wildcard"
                      checked={formData.pattern_type === "wildcard"}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pattern_type: e.target.value as "exact" | "wildcard",
                        })
                      }
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <Asterisk className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Wildcard
                    </span>
                  </label>
                </div>
              </div>

              {/* Origin Pattern */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Origin Pattern
                </label>
                <input
                  type="text"
                  value={formData.origin_pattern}
                  onChange={(e) =>
                    setFormData({ ...formData, origin_pattern: e.target.value })
                  }
                  placeholder={
                    formData.pattern_type === "exact"
                      ? "https://myapp.example.com"
                      : "*.web.app"
                  }
                  className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.pattern_type === "exact"
                    ? "Enter the full URL (e.g., https://myapp.example.com)"
                    : "Enter a wildcard pattern (e.g., *.web.app, http://localhost:*)"}
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="e.g., Firebase hosting for production app"
                  rows={2}
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                  <p className="text-xs text-gray-500">
                    Enable or disable this origin
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, is_active: !formData.is_active })
                  }
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    formData.is_active
                      ? "bg-indigo-600"
                      : "bg-gray-200 dark:bg-gray-700",
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      formData.is_active ? "translate-x-6" : "translate-x-1",
                    )}
                  />
                </button>
              </div>

              {/* Error in modal */}
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
              <button
                onClick={closeModal}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={creating || !formData.origin_pattern}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700 disabled:bg-indigo-400"
              >
                {creating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    {editingOrigin ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>{editingOrigin ? "Update Origin" : "Add Origin"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Origin Card Component
function OriginCard({
  origin,
  onEdit,
  onToggle,
  onDelete,
}: {
  origin: SSOOrigin;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div
              className={cn(
                "rounded-lg p-2",
                origin.pattern_type === "wildcard"
                  ? "bg-purple-100 dark:bg-purple-900/30"
                  : "bg-blue-100 dark:bg-blue-900/30",
              )}
            >
              {origin.pattern_type === "wildcard" ? (
                <Asterisk className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              ) : (
                <Link2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-900 dark:bg-gray-700 dark:text-white">
                  {origin.origin_pattern}
                </code>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-medium",
                    origin.is_active
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
                  )}
                >
                  {origin.is_active ? "Active" : "Inactive"}
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-medium",
                    origin.pattern_type === "wildcard"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                      : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                  )}
                >
                  {origin.pattern_type}
                </span>
              </div>
              {origin.description && (
                <p className="truncate text-sm text-gray-600 dark:text-gray-400">
                  {origin.description}
                </p>
              )}
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                {origin.org_name && (
                  <span className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {origin.org_name}
                  </span>
                )}
                {!origin.org_id && (
                  <span className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Global
                  </span>
                )}
                {origin.created_by && (
                  <span>Created by {origin.created_by}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggle}
              className={cn(
                "rounded-lg p-2 transition-colors",
                origin.is_active
                  ? "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                  : "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700",
              )}
              title={origin.is_active ? "Deactivate" : "Activate"}
            >
              {origin.is_active ? (
                <ToggleRight className="h-5 w-5" />
              ) : (
                <ToggleLeft className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={onEdit}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900/20"
              title="Edit"
            >
              <Edit2 className="h-5 w-5" />
            </button>
            <button
              onClick={onDelete}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
              title="Delete"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
